import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Trash2, ExternalLink, Save, ArrowLeft, FilePlus, Eye, EyeOff, Compass, MinusCircle, Archive, ArchiveRestore } from "lucide-react";
import { toast } from "sonner";
import client from "@/api/client";
import { useSiteContent } from "@/context/SiteContentContext";
import { BulkToolbar, useBulkSelection } from "@/components/admin/BulkToolbar";

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["blockquote", "code-block"],
    ["link"],
    [{ align: [] }],
    ["clean"],
  ],
};

const QUILL_FORMATS = [
  "header", "bold", "italic", "underline", "strike",
  "list", "bullet", "blockquote", "code-block", "link", "align",
];

function slugify(text) {
  return (text || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 80);
}

const BLANK_PAGE = {
  slug: "",
  title_en: "",
  title_ar: "",
  meta_desc_en: "",
  meta_desc_ar: "",
  content_en: "",
  content_ar: "",
  show_in_nav: true,
  nav_placement: "both",
  published: true,
  archived: false,
  footer_column: "company",
  is_post: false,
  cover_url: "",
  excerpt_en: "",
  excerpt_ar: "",
};

const PLACEMENTS = ["both", "header", "footer", "hidden"];

/** Delete confirmation that matches the rest of the console (no window.confirm). */
function ConfirmDelete({ title, body, cancelLabel, confirmLabel, onConfirm, children, testId }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
      <AlertDialogContent data-testid={testId}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{body}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`${testId}-cancel`}>{cancelLabel}</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={onConfirm}
            data-testid={`${testId}-confirm`}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function AdminPagesManager() {
  const { t } = useTranslation();
  const { refreshNavPages } = useSiteContent();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState("list"); // list | edit
  const [current, setCurrent] = useState(null);
  const [statusFilter, setStatusFilter] = useState("active"); // active | archived | all
  const [archivedCount, setArchivedCount] = useState(0);

  const loadPages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.get("/admin/pages", { params: { status: statusFilter } });
      setPages(res.data.items || []);
      setArchivedCount(res.data.archived_count || 0);
    } catch (e) {
      console.error("Load pages failed:", e);
      toast.error(t("common.error"));
    } finally {
      setLoading(false);
    }
  }, [t, statusFilter]);

  useEffect(() => { loadPages(); }, [loadPages]);

  const openNew = () => {
    setCurrent({ ...BLANK_PAGE });
    setMode("edit");
  };

  const openEdit = (page) => {
    setCurrent({ ...BLANK_PAGE, ...page });
    setMode("edit");
  };

  const closeEdit = () => {
    setCurrent(null);
    setMode("list");
  };

  const update = (field, value) => setCurrent((c) => ({ ...c, [field]: value }));

  const suggestedSlug = useMemo(() => slugify(current?.slug || current?.title_en || ""), [current?.slug, current?.title_en]);

  const save = async () => {
    if (!current?.title_en?.trim()) {
      toast.error(t("pages.titleRequired"));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        slug: (current.slug || suggestedSlug).trim() || suggestedSlug,
        title_en: current.title_en,
        title_ar: current.title_ar || "",
        meta_desc_en: current.meta_desc_en || "",
        meta_desc_ar: current.meta_desc_ar || "",
        content_en: current.content_en || "",
        content_ar: current.content_ar || "",
        show_in_nav: (current.nav_placement || "both") !== "hidden",
        nav_placement: current.nav_placement || "both",
        published: !!current.published,
        archived: !!current.archived,
        footer_column: current.footer_column || "company",
        is_post: !!current.is_post,
        cover_url: current.cover_url || "",
        excerpt_en: current.excerpt_en || "",
        excerpt_ar: current.excerpt_ar || "",
      };
      let res;
      if (current.page_id) {
        res = await client.put(`/admin/pages/${current.page_id}`, payload);
      } else {
        res = await client.post("/admin/pages", payload);
      }
      setCurrent(res.data);
      await loadPages();
      await refreshNavPages();
      toast.success(t("admin.saved"));
      // Return to the list so the updated footer-column badge / status is
      // immediately visible without an extra manual step.
      closeEdit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setSaving(false);
    }
  };

  const toggleArchive = async (page) => {
    try {
      await client.patch(`/admin/pages/${page.page_id}/archive`, null, {
        params: { archived: !page.archived },
      });
      await loadPages();
      await refreshNavPages();
      toast.success(t("admin.saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  };

  const removePage = async (page) => {
    try {
      await client.delete(`/admin/pages/${page.page_id}`);
      await loadPages();
      await refreshNavPages();
      toast.success(t("admin.saved"));
    } catch (e) {
      toast.error(t("common.error"));
    }
  };

  // Bulk selection — keyed by slug (custom_pages PK on the backend).
  const bulk = useBulkSelection("pages", (p) => p.slug, async () => { await loadPages(); await refreshNavPages(); });
  const allSelected = pages.length > 0 && pages.every((p) => bulk.isSelected(p.slug));
  const bulkActions = [
    { key: "publish", label: t("admin.published"), icon: <Eye className="h-3.5 w-3.5" /> },
    { key: "unpublish", label: t("admin.draft"), icon: <EyeOff className="h-3.5 w-3.5" /> },
    { key: "show_in_nav", label: t("admin.inNav"), icon: <Compass className="h-3.5 w-3.5" /> },
    { key: "hide_from_nav", label: t("pages.removeFromNav"), icon: <MinusCircle className="h-3.5 w-3.5" /> },
    {
      key: "delete",
      label: t("admin.delete"),
      icon: <Trash2 className="h-3.5 w-3.5" />,
      variant: "destructive",
      requiresConfirm: true,
      confirmTitle: t("pages.confirmDeleteBulkTitle"),
      confirmBody: t("pages.confirmDeleteBulkBody"),
    },
  ];

  if (mode === "edit" && current) {
    return (
      <Card className="rounded-2xl border border-border p-4 sm:p-6 space-y-5 min-w-0 overflow-hidden" data-testid="admin-page-editor">
        <div className="flex flex-wrap items-center justify-between gap-2 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <Button variant="ghost" size="sm" className="rounded-lg gap-1.5 text-muted-foreground" onClick={closeEdit}>
              <ArrowLeft className="h-4 w-4 rtl-flip" /> {t("common.back")}
            </Button>
            <h2 className="font-display text-base sm:text-xl font-semibold truncate min-w-0">
              {current.page_id ? current.title_en : t("admin.newPage")}
            </h2>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap min-w-0">
            {current.page_id && (
              <a href={`/p/${current.slug}`} target="_blank" rel="noreferrer" className="text-xs sm:text-sm text-primary inline-flex items-center gap-1 hover:underline max-w-[140px] sm:max-w-none truncate" data-testid="admin-page-preview-link">
                <ExternalLink className="h-3.5 w-3.5 shrink-0" /> <span className="truncate font-mono" dir="ltr">/p/{current.slug}</span>
              </a>
            )}
            {current.page_id && (
              <Button
                variant="outline"
                className="rounded-xl gap-2 h-9 px-3 text-xs sm:text-sm"
                onClick={() => toggleArchive(current).then(closeEdit)}
                data-testid="admin-page-archive-toggle"
              >
                {current.archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                {current.archived ? t("pages.restore") : t("pages.archive")}
              </Button>
            )}
            <Button onClick={save} disabled={saving} className="btn-brand rounded-xl gap-2 h-9 px-3 text-xs sm:text-sm" data-testid="admin-page-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {t("common.save")}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 min-w-0 [&>*]:min-w-0">
          <div>
            <Label htmlFor="pg-title-en">{t("admin.pageTitleLabel")} (EN)</Label>
            <Input id="pg-title-en" value={current.title_en} onChange={(e) => update("title_en", e.target.value)} className="mt-1.5 rounded-xl h-10" data-testid="admin-page-title-en" />
          </div>
          <div>
            <Label htmlFor="pg-title-ar">{t("admin.pageTitleLabel")} (AR)</Label>
            <Input id="pg-title-ar" dir="rtl" value={current.title_ar} onChange={(e) => update("title_ar", e.target.value)} className="mt-1.5 rounded-xl h-10" data-testid="admin-page-title-ar" />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_auto_auto] gap-4 items-end min-w-0 [&>*]:min-w-0">
          <div>
            <Label htmlFor="pg-slug">{t("admin.slug")}</Label>
            <Input id="pg-slug" value={current.slug || suggestedSlug} onChange={(e) => update("slug", slugify(e.target.value))} className="mt-1.5 rounded-xl h-10 font-mono text-sm" placeholder={t("pages.slugPlaceholder")} data-testid="admin-page-slug" />
            <p className="text-xs text-muted-foreground mt-1"><span className="font-mono">/p/{current.slug || suggestedSlug || "..."}</span></p>
          </div>
          <div className="flex items-center gap-2 h-10 mt-6">
            <Switch checked={!!current.published} onCheckedChange={(v) => update("published", v)} id="pg-published" data-testid="admin-page-published" />
            <Label htmlFor="pg-published" className="cursor-pointer">{t("admin.published")}</Label>
          </div>
          <div>
            <Label htmlFor="pg-placement" className="text-xs">{t("pages.placement")}</Label>
            <Select value={current.nav_placement || "both"} onValueChange={(v) => update("nav_placement", v)}>
              <SelectTrigger id="pg-placement" className="mt-1.5 rounded-xl h-10 w-full sm:w-[190px]" data-testid="admin-page-placement">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PLACEMENTS.map((v) => (
                  <SelectItem key={v} value={v}>{t(`pages.placement_${v}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {["both", "footer"].includes(current.nav_placement || "both") && (
          <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,240px)_1fr] gap-4 items-end min-w-0 [&>*]:min-w-0">
            <div>
              <Label htmlFor="pg-footer-col" className="text-xs">
                {t("admin.footerColumn", { defaultValue: "Footer column" })}
              </Label>
              <Select value={current.footer_column || "company"} onValueChange={(v) => update("footer_column", v)}>
                <SelectTrigger id="pg-footer-col" className="mt-1.5 rounded-xl h-10 w-full" data-testid="admin-page-footer-column">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="platform">{t("footer.platform")}</SelectItem>
                  <SelectItem value="company">{t("footer.company")}</SelectItem>
                  <SelectItem value="resources">{t("footer.resources")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground pb-2">{t("pages.footerColumnHelp")}</p>
          </div>
        )}

        {current.published && (current.nav_placement || "both") === "hidden" && (
          <div
            className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-xs text-amber-700 dark:text-amber-300"
            data-testid="admin-page-hidden-warning"
          >
            {t("pages.hiddenWarning", { slug: current.slug || suggestedSlug || "..." })}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="pg-meta-en">{t("admin.metaDesc")} (EN)</Label>
            <Input id="pg-meta-en" value={current.meta_desc_en} onChange={(e) => update("meta_desc_en", e.target.value)} className="mt-1.5 rounded-xl h-10" placeholder={t("pages.metaPlaceholder")} data-testid="admin-page-meta-en" />
          </div>
          <div>
            <Label htmlFor="pg-meta-ar">{t("admin.metaDesc")} (AR)</Label>
            <Input id="pg-meta-ar" dir="rtl" value={current.meta_desc_ar} onChange={(e) => update("meta_desc_ar", e.target.value)} className="mt-1.5 rounded-xl h-10" placeholder="لتحسين محركات البحث" data-testid="admin-page-meta-ar" />
          </div>
        </div>

        <div className="rounded-xl border border-border p-3.5 space-y-3" data-testid="admin-page-post-block">
          <label className="flex items-center gap-2.5 text-sm font-medium">
            <input
              type="checkbox"
              checked={!!current.is_post}
              onChange={(e) => update("is_post", e.target.checked)}
              data-testid="admin-page-is-post"
            />
            {t("pages.isPost", { defaultValue: "Show in the Insights blog (/blog)" })}
          </label>
          {current.is_post && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label htmlFor="pg-cover">{t("pages.coverUrl", { defaultValue: "Cover image URL (optional)" })}</Label>
                <Input id="pg-cover" dir="ltr" value={current.cover_url || ""} onChange={(e) => update("cover_url", e.target.value)} className="mt-1.5 rounded-xl h-10" placeholder="https://…" data-testid="admin-page-cover-url" />
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {t("pages.coverHint", { defaultValue: "Leave empty to use brand artwork automatically." })}
                </p>
              </div>
              <div>
                <Label htmlFor="pg-exc-en">{t("pages.excerpt", { defaultValue: "Excerpt" })} (EN)</Label>
                <Input id="pg-exc-en" value={current.excerpt_en || ""} onChange={(e) => update("excerpt_en", e.target.value)} className="mt-1.5 rounded-xl h-10" data-testid="admin-page-excerpt-en" />
              </div>
              <div>
                <Label htmlFor="pg-exc-ar">{t("pages.excerpt", { defaultValue: "Excerpt" })} (AR)</Label>
                <Input id="pg-exc-ar" dir="rtl" value={current.excerpt_ar || ""} onChange={(e) => update("excerpt_ar", e.target.value)} className="mt-1.5 rounded-xl h-10" data-testid="admin-page-excerpt-ar" />
              </div>
            </div>
          )}
        </div>

        <Separator />

        <Tabs defaultValue="content_en">
          <TabsList className="rounded-xl">
            <TabsTrigger value="content_en" className="rounded-lg" data-testid="admin-page-tab-en">{t("admin.contentEn")}</TabsTrigger>
            <TabsTrigger value="content_ar" className="rounded-lg" data-testid="admin-page-tab-ar">{t("admin.contentAr")}</TabsTrigger>
          </TabsList>
          <TabsContent value="content_en" className="mt-4">
            <div className="rounded-xl border border-input overflow-hidden" data-testid="admin-page-editor-en">
              <ReactQuill
                theme="snow"
                value={current.content_en || ""}
                onChange={(html) => update("content_en", html)}
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
              />
            </div>
          </TabsContent>
          <TabsContent value="content_ar" className="mt-4">
            <div className="rounded-xl border border-input overflow-hidden" dir="rtl" data-testid="admin-page-editor-ar">
              <ReactQuill
                theme="snow"
                value={current.content_ar || ""}
                onChange={(html) => update("content_ar", html)}
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
              />
            </div>
          </TabsContent>
        </Tabs>
      </Card>
    );
  }

  return (
    <Card className="rounded-2xl border border-border p-6 space-y-4" data-testid="admin-panel-custom-pages">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl font-semibold">{t("admin.customPages")}</h2>
          <p className="text-sm text-muted-foreground mt-1">{t("admin.customPagesDesc")}</p>
        </div>
        <Button className="btn-brand rounded-xl gap-2" onClick={openNew} data-testid="admin-page-new">
          <FilePlus className="h-4 w-4" /> {t("admin.newPage")}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-1.5" data-testid="admin-pages-status-filter">
        {["active", "archived", "all"].map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setStatusFilter(f)}
            className={`rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${
              statusFilter === f
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-background text-muted-foreground border-border hover:border-primary/40"
            }`}
            data-testid={`admin-pages-filter-${f}`}
          >
            {t(`pages.filter_${f}`)}
            {f === "archived" && archivedCount > 0 && <span className="ms-1 font-mono opacity-70">{archivedCount}</span>}
          </button>
        ))}
      </div>

      <BulkToolbar {...bulk} actions={bulkActions} testId="admin-pages-bulk" onClear={bulk.clear} />

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : pages.length === 0 ? (
        <p className="text-sm text-muted-foreground py-10 text-center" data-testid="admin-pages-empty">{t("admin.customPagesEmpty")}</p>
      ) : (
        <>
        {/* Phones get a card list; the table (with its 5 columns) only makes
            sense from md: up. */}
        <ul className="md:hidden space-y-2" data-testid="admin-pages-mobile-list">
          {pages.map((p) => {
            const placement = p.nav_placement || (p.show_in_nav ? "both" : "hidden");
            return (
              <li key={p.page_id} className="rounded-xl border border-border p-3" data-testid={`admin-page-card-${p.slug}`}>
                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={bulk.isSelected(p.slug)}
                    onChange={() => bulk.toggle(p.slug)}
                    aria-label={t("common.selectAll")}
                    data-testid={`admin-page-card-select-${p.slug}`}
                  />
                  <button type="button" onClick={() => openEdit(p)} className="min-w-0 flex-1 text-start" data-testid={`admin-page-card-open-${p.slug}`}>
                    <p className="font-medium text-sm truncate">{p.title_en}</p>
                    <p className="font-mono text-[11px] text-muted-foreground truncate">/p/{p.slug}</p>
                  </button>
                  <div className="flex items-center gap-0.5 shrink-0">
                    <a href={`/p/${p.slug}`} target="_blank" rel="noreferrer" className="h-8 w-8 grid place-items-center text-muted-foreground" data-testid={`admin-page-card-preview-${p.slug}`}>
                      <ExternalLink className="h-4 w-4" />
                    </a>
                    <button type="button" onClick={() => toggleArchive(p)} className="h-8 w-8 grid place-items-center text-muted-foreground" data-testid={`admin-page-card-archive-${p.slug}`}>
                      {p.archived ? <ArchiveRestore className="h-4 w-4 text-primary" /> : <Archive className="h-4 w-4" />}
                    </button>
                    <ConfirmDelete
                      title={t("pages.confirmDelete", { title: p.title_en })}
                      body={t("common.cannotUndo")}
                      cancelLabel={t("common.cancel")}
                      confirmLabel={t("admin.delete")}
                      onConfirm={() => removePage(p)}
                      testId={`admin-page-card-delete-dialog-${p.slug}`}
                    >
                      <button type="button" className="h-8 w-8 grid place-items-center" data-testid={`admin-page-card-delete-${p.slug}`}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </button>
                    </ConfirmDelete>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-1.5 mt-2">
                  <Badge variant="secondary" className="rounded-full text-[10px]">
                    {p.published ? t("admin.published") : t("admin.draft")}
                  </Badge>
                  {p.archived && (
                    <Badge variant="outline" className="rounded-full text-[10px] border-amber-500/40 text-amber-700">
                      {t("pages.archived")}
                    </Badge>
                  )}
                  <Badge variant="outline" className="rounded-full text-[10px]">{t(`pages.placement_${placement}`)}</Badge>
                </div>
              </li>
            );
          })}
        </ul>
        <div className="hidden md:block overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={() => bulk.toggleAll(pages)}
                    aria-label={t("common.selectAll")}
                    data-testid="admin-pages-select-all"
                  />
                </TableHead>
                <TableHead>{t("admin.pageTitleLabel")}</TableHead>
                <TableHead>{t("admin.slug")}</TableHead>
                <TableHead>{t("admin.status")}</TableHead>
                <TableHead className="text-end">{t("common.filter")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pages.map((p) => (
                <TableRow key={p.page_id} data-testid="admin-page-row">
                  <TableCell>
                    <input
                      type="checkbox"
                      checked={bulk.isSelected(p.slug)}
                      onChange={() => bulk.toggle(p.slug)}
                      aria-label={`Select ${p.title_en}`}
                      data-testid={`admin-pages-select-${p.slug}`}
                    />
                  </TableCell>
                  <TableCell>
                    <button type="button" onClick={() => openEdit(p)} className="font-medium text-start hover:text-primary transition-colors">
                      {p.title_en}
                    </button>
                    {p.title_ar && <p className="text-xs text-muted-foreground" dir="rtl">{p.title_ar}</p>}
                  </TableCell>
                  <TableCell className="font-mono text-xs">/p/{p.slug}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Badge variant="secondary" className="rounded-full">
                        {p.published ? t("admin.published") : t("admin.draft")}
                      </Badge>
                      {p.archived && (
                        <Badge variant="outline" className="rounded-full text-[10px] border-amber-500/40 text-amber-700" data-testid={`admin-page-archived-badge-${p.slug}`}>
                          {t("pages.archived")}
                        </Badge>
                      )}
                      <Badge variant="outline" className="rounded-full text-[10px]" data-testid={`admin-page-placement-badge-${p.slug}`}>
                        {t(`pages.placement_${p.nav_placement || (p.show_in_nav ? "both" : "hidden")}`)}
                      </Badge>
                      {["both", "footer"].includes(p.nav_placement || (p.show_in_nav ? "both" : "hidden")) && (
                        <Badge variant="outline" className="rounded-full text-[10px] capitalize text-muted-foreground" data-testid={`admin-page-col-badge-${p.slug}`}>
                          {p.footer_column === "platform" ? t("footer.platform")
                            : p.footer_column === "resources" ? t("footer.resources")
                            : t("footer.company")}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 justify-end">
                      <a href={`/p/${p.slug}`} target="_blank" rel="noreferrer" className="h-9 w-9 grid place-items-center text-muted-foreground hover:text-primary" title="Preview">
                        <ExternalLink className="h-4 w-4" />
                      </a>
                      <Button size="sm" variant="outline" className="rounded-lg" onClick={() => openEdit(p)} data-testid="admin-page-edit">
                        {t("common.edit")}
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => toggleArchive(p)}
                        title={p.archived ? t("pages.restore") : t("pages.archive")}
                        data-testid={`admin-page-archive-${p.slug}`}
                      >
                        {p.archived ? <ArchiveRestore className="h-4 w-4 text-primary" /> : <Archive className="h-4 w-4 text-muted-foreground" />}
                      </Button>
                      <ConfirmDelete
                        title={t("pages.confirmDelete", { title: p.title_en })}
                        body={t("common.cannotUndo")}
                        cancelLabel={t("common.cancel")}
                        confirmLabel={t("admin.delete")}
                        onConfirm={() => removePage(p)}
                        testId={`admin-page-delete-dialog-${p.slug}`}
                      >
                      <Button size="icon" variant="ghost" title={t("admin.delete")} data-testid="admin-page-delete">
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                      </ConfirmDelete>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        </>
      )}
    </Card>
  );
}
